import { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { SiteContentContext } from "../context/SiteContentContext";
import {
  AREA_SERVED,
  absoluteUrl,
  DEFAULT_COMPANY_EMAIL,
  DEFAULT_COMPANY_NAME,
  DEFAULT_COMPANY_PHONE,
  LOGO_URL,
  ORGANIZATION_ID,
  ORGANIZATION_NUMBER,
  SAME_AS,
  SITE_ORIGIN,
} from "../seoUtils";
import { OFFICES } from "./swedenMap.jsx";

const OPENING_HOURS = {
  "@type": "OpeningHoursSpecification",
  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  opens: "09:00",
  closes: "17:00",
};

/**
 * Address of one office, built from admin-editable site content.
 * Empty fields are omitted entirely — an empty `postalCode` is worse than no postalCode.
 */
function officeAddress(office, getContent) {
  const street = getContent(`office.${office.key}.street`, office.street);
  const postalCode = getContent(`office.${office.key}.postal_code`, "");
  const city = getContent(`office.${office.key}.city`, office.city);

  return {
    "@type": "PostalAddress",
    ...(street ? { streetAddress: street } : {}),
    ...(postalCode ? { postalCode } : {}),
    ...(city ? { addressLocality: city } : {}),
    addressCountry: "SE",
  };
}

/**
 * Company-level structured data: one Organization plus one LocalBusiness per office,
 * emitted as a single @graph so the offices are linked to the org via parentOrganization.
 *
 * Rendered on the home page and the contact page. All contact details come from site content
 * so admins can change them in one place (`company.*`, `office.*`).
 */
export default function OrganizationJsonLd({ locale }) {
  const { getContent } = useContext(SiteContentContext);

  const name = getContent("company.name", DEFAULT_COMPANY_NAME);
  const telephone = getContent("company.phone", DEFAULT_COMPANY_PHONE);
  const email = getContent("company.email", DEFAULT_COMPANY_EMAIL);
  const description = getContent(
    "footer.company_description",
    "Ledande leverantör av maskiner för trä- och plastindustrin."
  );

  // The Stockholm office is the registered seat, so its address doubles as the org address.
  const headOffice = OFFICES[0];

  const organization = {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name,
    legalName: DEFAULT_COMPANY_NAME,
    url: absoluteUrl(locale, "/"),
    logo: LOGO_URL,
    image: LOGO_URL,
    foundingDate: "2024",
    description,
    telephone,
    email,
    identifier: {
      "@type": "PropertyValue",
      propertyID: "Organisationsnummer",
      value: ORGANIZATION_NUMBER,
    },
    taxID: ORGANIZATION_NUMBER,
    address: officeAddress(headOffice, getContent),
    areaServed: AREA_SERVED,
    contactPoint: {
      "@type": "ContactPoint",
      telephone,
      email,
      contactType: "sales",
      areaServed: AREA_SERVED,
      availableLanguage: ["Swedish"],
    },
    sameAs: SAME_AS,
  };

  const localBusinesses = OFFICES.map((office) => {
    const city = getContent(`office.${office.key}.city`, office.city);
    return {
      "@type": "LocalBusiness",
      "@id": `${SITE_ORIGIN}#office-${office.key}`,
      name: `${name} – ${city}`,
      parentOrganization: { "@id": ORGANIZATION_ID },
      url: absoluteUrl(locale, "/contact"),
      image: LOGO_URL,
      telephone,
      email,
      priceRange: "$$",
      address: officeAddress(office, getContent),
      areaServed: AREA_SERVED,
      openingHoursSpecification: OPENING_HOURS,
    };
  });

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [organization, ...localBusinesses],
        })}
      </script>
    </Helmet>
  );
}
